import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertCircle,
  RefreshCw,
  Rocket,
  Package,
  CheckCircle,
  XCircle,
  Eye,
  Copy,
  Save,
} from 'lucide-react';
import {
  fetchOdooVersions,
  fetchOdooVersion,
  updateOdooVersion,
  toggleOdooVersion,
  rebuildOdooVersion,
  rebuildAllOdooVersions,
} from '@/api/admin';
import type { OdooVersionConfig, EnterpriseArchive } from '@/types/admin';
import { toast } from 'sonner';

export function AdminOdooVersionsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-odoo-versions'],
    queryFn: fetchOdooVersions,
  });

  const [editing, setEditing] = useState<Record<number, Partial<OdooVersionConfig & { auto_rebuild?: boolean }>>>({});
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  const { data: versionDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ['admin-odoo-version-details', selectedVersionId],
    queryFn: () => fetchOdooVersion(selectedVersionId!),
    enabled: selectedVersionId !== null && detailsDialogOpen,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) => updateOdooVersion(id, payload),
    onSuccess: () => {
      toast.success('Configuration updated');
      queryClient.invalidateQueries({ queryKey: ['admin-odoo-versions'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update'),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => toggleOdooVersion(id),
    onSuccess: () => {
      toast.success('Version toggled');
      queryClient.invalidateQueries({ queryKey: ['admin-odoo-versions'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to toggle'),
  });

  const rebuildMutation = useMutation({
    mutationFn: (id: number) => rebuildOdooVersion(id),
    onSuccess: () => toast.success('Rebuild triggered'),
    onError: (err: any) => toast.error(err.message || 'Failed to rebuild'),
  });

  const rebuildAllMutation = useMutation({
    mutationFn: rebuildAllOdooVersions,
    onSuccess: () => toast.success('Rebuild triggered for all active versions'),
    onError: (err: any) => toast.error(err.message || 'Failed to rebuild'),
  });

  const configs = data?.configs || [];
  const enterpriseArchives = data?.enterprise_archives || {};

  const handleSave = (config: OdooVersionConfig) => {
    const edit = editing[config.id] || {};
    if (Object.keys(edit).length === 0) {
      toast.info('No changes to save');
      return;
    }
    updateMutation.mutate({ id: config.id, payload: edit });
  };

  const openDetailsDialog = (configId: number) => {
    setSelectedVersionId(configId);
    setDetailsDialogOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleRebuild = (configId: number, version: string) => {
    if (confirm(`Rebuild enterprise archive for Odoo ${version}? This may take several minutes.`)) {
      rebuildMutation.mutate(configId);
    }
  };

  const handleRebuildAll = () => {
    if (confirm('Rebuild all active Odoo versions? This may take a while.')) {
      rebuildAllMutation.mutate();
    }
  };

  const activeCount = configs.filter((c) => c.is_active).length;

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Odoo Versions</h1>
          <p className="text-sm text-muted-foreground">
            Manage Odoo nightly builds and enterprise archives
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRebuildAll}
          disabled={rebuildAllMutation.isPending}
        >
          <Rocket className="h-4 w-4 mr-2" />
          {rebuildAllMutation.isPending ? 'Rebuilding...' : 'Rebuild All Active'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Versions</p>
                <p className="text-2xl font-semibold">{configs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-semibold">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <XCircle className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-2xl font-semibold">{configs.length - activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <RefreshCw className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">With Archives</p>
                <p className="text-2xl font-semibold">
                  {Object.values(enterpriseArchives).filter(Boolean).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load versions.</AlertDescription>
        </Alert>
      )}

      {/* Version Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          configs.map((config) => {
            const edit = editing[config.id] || {};
            const archive = enterpriseArchives[config.version];

            return (
              <Card key={config.id} className={!config.is_active ? 'opacity-60' : ''}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle>Odoo {config.version}</CardTitle>
                      {config.is_active ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDetailsDialog(config.id)}
                        title="View archive history"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleMutation.mutate(config.id)}
                      >
                        {config.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Commit IDs */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Odoo Commit</Label>
                      <Input
                        placeholder="HEAD"
                        value={edit.odoo_commit_id ?? config.odoo_commit_id ?? ''}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [config.id]: { ...prev[config.id], odoo_commit_id: e.target.value },
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Enterprise Commit</Label>
                      <Input
                        placeholder="HEAD"
                        value={edit.enterprise_commit_id ?? config.enterprise_commit_id ?? ''}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [config.id]: { ...prev[config.id], enterprise_commit_id: e.target.value },
                          }))
                        }
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-xs">Notes</Label>
                    <Input
                      placeholder="Optional notes"
                      value={edit.notes ?? config.notes ?? ''}
                      onChange={(e) =>
                        setEditing((prev) => ({
                          ...prev,
                          [config.id]: { ...prev[config.id], notes: e.target.value },
                        }))
                      }
                    />
                  </div>

                  {/* CI Test Modules */}
                  <div className="space-y-2">
                    <Label className="text-xs">CI Test Modules</Label>
                    <Textarea
                      placeholder="base,sale,purchase,stock,account (comma-separated)"
                      value={edit.ci_test_modules ?? config.ci_test_modules ?? ''}
                      onChange={(e) =>
                        setEditing((prev) => ({
                          ...prev,
                          [config.id]: { ...prev[config.id], ci_test_modules: e.target.value },
                        }))
                      }
                      rows={2}
                      className="text-xs"
                    />
                    <p className="text-xs text-muted-foreground">
                      When set, these modules are installed/tested during Continuous Testing runs instead of automatic detection.
                    </p>
                  </div>

                  {/* Auto-rebuild toggle */}
                  <div className="flex items-center gap-3">
                    <Switch
                      id={`auto-rebuild-${config.id}`}
                      checked={edit.auto_rebuild ?? false}
                      onCheckedChange={(checked) =>
                        setEditing((prev) => ({
                          ...prev,
                          [config.id]: { ...prev[config.id], auto_rebuild: checked },
                        }))
                      }
                    />
                    <Label htmlFor={`auto-rebuild-${config.id}`} className="text-sm">
                      Auto-rebuild (prevent race conditions)
                    </Label>
                  </div>

                  {/* Archive info */}
                  {archive && (
                    <div className="bg-muted p-3 rounded-lg text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Latest Archive:</span>
                        <div className="flex items-center gap-2">
                          <code className="bg-background px-2 py-0.5 rounded">
                            {archive.sha?.substring(0, 8) || 'unknown'}
                          </code>
                          {archive.sha && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => copyToClipboard(archive.sha!)}
                              title="Copy SHA"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Created:</span>
                        <span>{formatDate(archive.created_at)}</span>
                      </div>
                    </div>
                  )}

                  {/* Last built info */}
                  {config.last_built_at && (
                    <p className="text-xs text-muted-foreground">
                      Last built: {formatDate(config.last_built_at)}
                      {config.last_built_by && ` by ${config.last_built_by}`}
                    </p>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSave(config)}
                      disabled={updateMutation.isPending}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRebuild(config.id, config.version)}
                      disabled={rebuildMutation.isPending}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Rebuild
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Archive History - Odoo {versionDetails?.config?.version || '...'}
            </DialogTitle>
            <DialogDescription>
              View all enterprise archives for this version
            </DialogDescription>
          </DialogHeader>

          {detailsLoading ? (
            <p className="text-sm text-muted-foreground py-4">Loading...</p>
          ) : versionDetails ? (
            <div className="space-y-4 py-4">
              {/* Config Info */}
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Odoo Commit:</span>
                    <code className="ml-2 bg-background px-2 py-0.5 rounded text-xs">
                      {versionDetails.config.odoo_commit_id || 'HEAD'}
                    </code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Enterprise Commit:</span>
                    <code className="ml-2 bg-background px-2 py-0.5 rounded text-xs">
                      {versionDetails.config.enterprise_commit_id || 'HEAD'}
                    </code>
                  </div>
                </div>
                {versionDetails.config.notes && (
                  <p className="text-sm text-muted-foreground">
                    Notes: {versionDetails.config.notes}
                  </p>
                )}
              </div>

              {/* Archives Table */}
              {versionDetails.archives && versionDetails.archives.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b">
                        <th className="py-2 px-2 font-medium">Commit SHA</th>
                        <th className="py-2 px-2 font-medium">Status</th>
                        <th className="py-2 px-2 font-medium">Created</th>
                        <th className="py-2 px-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versionDetails.archives.map((archive: EnterpriseArchive) => (
                        <tr key={archive.id} className="border-b">
                          <td className="py-2 px-2">
                            <code className="bg-muted px-2 py-0.5 rounded text-xs">
                              {archive.sha?.substring(0, 12) || 'unknown'}
                            </code>
                          </td>
                          <td className="py-2 px-2">
                            {archive.is_active ? (
                              <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                                Active
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </td>
                          <td className="py-2 px-2 text-muted-foreground">
                            {formatDate(archive.created_at)}
                          </td>
                          <td className="py-2 px-2">
                            {archive.sha && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyToClipboard(archive.sha!)}
                                title="Copy full SHA"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No archives found for this version.</p>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
